import React from "react";
import {
  FaBalanceScale,
  FaBan,
  FaCheckCircle,
  FaFileContract,
  FaGavel,
  FaRobot,
  FaSyncAlt,
} from "react-icons/fa";
import PublicLayout from "../components/site/PublicLayout";
import { Button, Container } from "../ui";

// Mode-creation terms — linked from the mobile app's CreateMode screen
// (creating a mode = accepting these terms) and from the site footer.
const ModeCreationTerms = () => {
  const sections = [
    {
      icon: FaFileContract,
      title: "1. قبول الشروط",
      body: (
        <p>
          بإنشائك فئة (وضع مخصص) داخل تطبيق تربع، فأنت توافق على هذه الشروط
          بالكامل. إذا كنت لا توافق على أي بند منها، يرجى عدم إنشاء فئات أو
          إضافة محتوى إلى التطبيق.
        </p>
      ),
    },
    {
      icon: FaBan,
      title: "2. المحتوى المسموح والممنوع",
      body: (
        <>
          <p>
            أنت مسؤول عن كل محتوى تضيفه (أسماء الفئات، الأسئلة، الإجابات،
            الصور، الصوت، الفيديو). يُمنع منعًا باتًا نشر أي محتوى:
          </p>
          <ul>
            <li>مسيء أو جارح أو يحتوي على ألفاظ نابية</li>
            <li>جنسي أو غير لائق</li>
            <li>يحرّض على الكراهية أو العنف أو التنمر</li>
            <li>يكشف معلومات شخصية عن الآخرين (أرقام هواتف، إيميلات، حسابات)</li>
            <li>احتيالي أو دعائي أو مضلل</li>
            <li>ينتهك حقوق الملكية الفكرية لأي طرف</li>
          </ul>
        </>
      ),
    },
    {
      icon: FaRobot,
      title: "3. نظام الكشف التلقائي",
      body: (
        <>
          <p>
            يخضع كل محتوى يُضاف إلى التطبيق لفحص تلقائي بواسطة نظام الكشف
            الآلي (نصوص، صور، صوت، فيديو). عند رصد محتوى مخالف:
          </p>
          <ul>
            <li>يتم إخفاء أو حظر السؤال المخالف تلقائيًا، ويصلك إشعار بذلك</li>
            <li>
              إذا تكرر المحتوى المخالف داخل الفئة الواحدة (وصول عدد الأسئلة
              المحظورة أو المرفوضة إلى 10)، يتم حظر الفئة بالكامل تلقائيًا
            </li>
            <li>
              إذا تكرر حظر فئاتك (3 فئات محظورة)، يتم حظر حسابك تلقائيًا
            </li>
            <li>
              المحتوى الذي يصل إليه عدد كبير من البلاغات من المستخدمين قد
              يُحظر تلقائيًا أيضًا
            </li>
          </ul>
        </>
      ),
    },
    {
      icon: FaBalanceScale,
      title: "4. حق الاعتراض",
      body: (
        <p>
          نظام الكشف التلقائي قد يخطئ أحيانًا. إذا كنت ترى أن سؤالك أو فئتك أو
          حسابك حُظر عن طريق الخطأ (اكتشاف خاطئ)، يمكنك تقديم اعتراض مباشرة من
          داخل التطبيق — من شاشة السؤال أو الفئة المحظورة أو من مركز
          الإشعارات. تتم مراجعة كل اعتراض من قبل فريق الإشراف، وفي حال قبوله
          يُستعاد المحتوى ويُرفع الحظر تلقائيًا.
        </p>
      ),
    },
    {
      icon: FaGavel,
      title: "5. الملكية والترخيص",
      body: (
        <p>
          تحتفظ بملكية المحتوى الذي تنشئه، وتمنح تربع ترخيصًا غير حصري لعرض
          هذا المحتوى داخل التطبيق ومشاركته مع المستخدمين الذين تشاركهم فئتك
          أو — في حال الفئات العامة — مع مجتمع التطبيق. يحق لتربع إزالة أي
          محتوى مخالف في أي وقت.
        </p>
      ),
    },
    {
      icon: FaSyncAlt,
      title: "6. تعديل الشروط",
      body: (
        <p>
          قد يتم تحديث هذه الشروط من وقت لآخر. استمرارك في إنشاء الفئات أو
          إضافة المحتوى بعد أي تعديل يعني موافقتك على الشروط المحدّثة.
        </p>
      ),
    },
  ];

  return (
    <PublicLayout>
      <Container narrow className="py-10 md:py-16">
        <article className="max-w-2xl mx-auto">
          {/* Header */}
          <header className="text-center mb-10 md:mb-12">
            <div className="w-14 h-14 rounded-2xl bg-primary/15 text-primary flex items-center justify-center mx-auto mb-5">
              <FaFileContract className="text-2xl" aria-hidden="true" />
            </div>
            <h1 className="text-3xl md:text-4xl font-extrabold leading-tight">
              شروط إنشاء الفئات والمحتوى
            </h1>
            <p className="text-white/70 text-base md:text-lg mt-3 leading-relaxed">
              اقرأ هذه الشروط قبل إنشاء فئة جديدة في تربع. بإنشائك فئة فأنت
              توافق عليها.
            </p>
          </header>

          <div className="space-y-8">
            {sections.map((section, index) => (
              <section
                key={index}
                className="border-t border-white/10 pt-8 first:border-t-0 first:pt-0"
              >
                <h2 className="text-lg md:text-xl font-extrabold mb-3 flex items-center gap-3">
                  <span className="w-9 h-9 shrink-0 rounded-xl bg-white/[0.07] border border-white/10 text-primary flex items-center justify-center">
                    <section.icon className="text-base" aria-hidden="true" />
                  </span>
                  {section.title}
                </h2>
                <div className="text-white/75 leading-loose space-y-3 [&_ul]:list-disc [&_ul]:pr-6 [&_ul]:space-y-1.5 [&_li]:marker:text-primary">
                  {section.body}
                </div>
              </section>
            ))}
          </div>

          <div className="mt-10 rounded-2xl bg-success/10 border border-success/30 p-4 md:p-5 flex items-start gap-3">
            <FaCheckCircle
              className="text-success shrink-0 mt-1"
              aria-hidden="true"
            />
            <p className="text-white/85 text-sm md:text-base leading-relaxed">
              هدف هذه الشروط هو الحفاظ على بيئة لعب ممتعة وآمنة للجميع.
              الغالبية العظمى من الفئات لا تتأثر بأي من هذه الإجراءات.
            </p>
          </div>

          <div className="mt-10 text-center">
            <Button to="/" variant="secondary">
              العودة إلى الرئيسية
            </Button>
          </div>
        </article>
      </Container>
    </PublicLayout>
  );
};

export default ModeCreationTerms;
